/**
 * RevOps Context Provider
 * Manages revenue operations data including subscriptions, invoices, and analytics
 * Integrates with Sales Pipeline for won deals and organization data
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { apiClient } from '@/services/apiClient';
import { salesPipelineAPI } from '@/services/api/salesPipelineAPI';
import { invoiceAPI, ServerInvoiceTemplate, Invoice as APIInvoice, centsToDecimal } from '@/services/api/invoiceAPI';

// ============================================================================
// TYPES
// ============================================================================

// Organization from /api/cbos
export interface Organization {
  id: number;
  name: string;
  short_name?: string;
  customer_status?: string;
  created_at?: string;
  city?: string;
  state?: string;
  website?: string;
  logo_url?: string;
  billing_email?: string;
  billing_address?: string;
  tax_id?: string;
}

// Pipeline opportunity with pricing details
export interface PipelineOpportunity {
  id: string;
  name: string;
  company: string;
  companyId?: number;
  amount: number;
  stage: string;
  probability: number;
  closeDate: string;
  owner: string;
  lastActivity: string;
  source?: string;
  pipelineId?: string;
  lineItems?: OpportunityLineItem[];
  financials?: OpportunityFinancials;
  contractTermMonths?: number;
}

export interface OpportunityLineItem {
  id: string;
  productId?: string;
  itemName: string;
  description?: string;
  pricingModel: string;
  quantity: number;
  unitPrice: number;
  billingType: string;
  billingInterval?: string;
  termMonths?: number;
  discountType?: 'percent' | 'fixed';
  discountValue: number;
  estimatedTransactionVolume?: number;
  transactionRate?: number;
  estimatedOutcomes?: number;
  pricePerOutcome?: number;
  estimatedUsage?: number;
  usagePrice?: number;
  percentageRate?: number;
  percentageBasis?: string;
  estimatedBasisAmount?: number;
  isCredit?: boolean;
  lineTotal: number;
  discountAmount: number;
  netAmount: number;
  monthlyRecurring: number;
  annualRecurring: number;
}

export interface OpportunityFinancials {
  totalOneTime: number;
  totalMrr: number;
  totalArr: number;
  estimatedUsageRevenue: number;
  estimatedTransactionRevenue: number;
  estimatedOutcomeRevenue: number;
  estimatedPercentageRevenue: number;
  totalCredits: number;
  subtotal: number;
  totalDiscount: number;
  grandTotal: number;
  totalContractValue: number;
}

// Product from sales pipeline
export interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  category?: string;
  pricingModel: string;
  unitPrice: number;
  billingType: string;
  billingInterval?: string;
  priceTiers?: PriceTier[];
  volumeThresholds?: VolumeThreshold[];
  transactionRate?: number;
  usageRate?: number;
  minimumFee?: number;
  percentageRate?: number;
  percentageBasis?: string;
  isCredit?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PriceTier {
  id: string;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
  flatFee?: number;
}

export interface VolumeThreshold {
  id: string;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
}

// RevOps Subscription (created from won deals or manually)
export interface RevOpsSubscription {
  id: string;
  customer: string;
  customerId?: number;
  opportunityId?: string;
  plan: string;
  mrr: number;
  arr: number;
  status: 'active' | 'past_due' | 'cancelled' | 'trialing' | 'pending';
  startDate: string;
  renewalDate: string;
  contractTermMonths?: number;
  billingInterval?: string;
  lineItems?: SubscriptionLineItem[];
  oneTimeRevenue?: number;
  estimatedUsageRevenue?: number;
  estimatedTransactionRevenue?: number;
  estimatedOutcomeRevenue?: number;
  organization?: Organization;
  createdAt?: string;
}

export interface SubscriptionLineItem {
  id: string;
  itemName: string;
  description?: string;
  pricingModel: string;
  quantity: number;
  unitPrice: number;
  billingType: string;
  billingInterval?: string;
  transactionRate?: number;
  pricePerOutcome?: number;
  monthlyRecurring: number;
  annualRecurring: number;
}

// RevOps Invoice
export interface RevOpsInvoice {
  id: string;
  invoiceNumber: string;
  customer: string;
  customerId?: number;
  subscriptionId?: string;
  opportunityId?: string;
  amount: number;
  subtotal: number;
  tax?: number;
  discount?: number;
  status: 'paid' | 'pending' | 'overdue' | 'draft' | 'void' | 'sent';
  dueDate: string;
  issuedDate: string;
  paidDate?: string;
  lineItems?: InvoiceLineItem[];
  notes?: string;
  terms?: string;
  organization?: Organization;
  // Branding
  template?: InvoiceTemplate;
  createdAt?: string;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  pricingModel?: string;
  billingType?: string;
}

// Invoice Template with enhanced branding and payment options
export interface InvoiceTemplate {
  id: string;
  name: string;
  description?: string;
  // Logo & Branding
  logoUrl?: string;
  companyName: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  companyWebsite?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  fontFamily?: string;
  // Banking & Payment Information
  bankName?: string;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankRoutingNumber?: string;
  bankSwiftCode?: string;
  bankIban?: string;
  paypalEmail?: string;
  stripeEnabled?: boolean;
  acceptedPaymentMethods?: ('bank_transfer' | 'credit_card' | 'check' | 'paypal' | 'ach' | 'wire')[];
  paymentMethodInstructions?: Record<string, string>;
  // Content
  footerText?: string;
  paymentInstructions?: string;
  termsAndConditions?: string;
  defaultDueDays?: number;
  defaultNotes?: string;
  // Display options
  showLogo: boolean;
  showPaymentInstructions: boolean;
  showBankDetails: boolean;
  showTaxId: boolean;
  showPortalLink: boolean;
  taxIdLabel?: string;
  taxId?: string;
  // Numbering
  invoicePrefix?: string;
  invoiceNumberFormat?: string;
  // Organization association
  organizationId?: number;
  isDefault: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Tax configuration by jurisdiction
export interface TaxConfiguration {
  id: string;
  name: string;
  rate: number; // as percentage, e.g., 8.25
  type: 'sales_tax' | 'vat' | 'gst' | 'hst' | 'pst' | 'service_tax' | 'custom';
  jurisdiction: string; // state, country, or region
  country: string;
  state?: string;
  city?: string;
  zipCode?: string;
  appliesTo: ('products' | 'services' | 'all')[];
  isCompound?: boolean; // applies on top of other taxes
  isInclusive?: boolean; // tax included in price
  isActive: boolean;
}

// Tax rates by service category
export interface ServiceTaxRule {
  id: string;
  serviceCategory: string;
  description?: string;
  defaultTaxable: boolean;
  exemptionCodes?: string[];
  specialRates?: { jurisdiction: string; rate: number }[];
}

// Invoice line item with tax support
export interface InvoiceLineItemWithTax extends InvoiceLineItem {
  taxable: boolean;
  taxRate?: number;
  taxAmount?: number;
  serviceCategory?: string;
  serviceLocation?: string;
}

// Analytics data structures
export interface RevenueByPricingModel {
  model: string;
  label: string;
  mrr: number;
  percentage: number;
  color: string;
}

export interface MRRMovement {
  type: 'new' | 'expansion' | 'contraction' | 'churn' | 'reactivation';
  label: string;
  amount: number;
  count: number;
}

export interface CohortData {
  cohort: string;
  month0: number;
  month1: number;
  month2: number;
  month3: number;
  month6: number;
  month12: number;
}

export interface RevenueForecast {
  month: string;
  projected: number;
  optimistic: number;
  conservative: number;
}

// Account (manually entered or from won deals)
export interface RevOpsAccount {
  id: string;
  organizationId?: number;
  name: string;
  status: 'active' | 'churned' | 'pending';
  mrr: number;
  arr: number;
  startDate: string;
  source: 'pipeline_won' | 'manual_entry';
  opportunityId?: string;
  contractTermMonths?: number;
  organization?: Organization;
}

// ============================================================================
// CONTEXT TYPE
// ============================================================================

interface RevOpsContextValue {
  // Data
  organizations: Organization[];
  products: Product[];
  wonDeals: PipelineOpportunity[];
  subscriptions: RevOpsSubscription[];
  invoices: RevOpsInvoice[];
  accounts: RevOpsAccount[];
  invoiceTemplates: InvoiceTemplate[];

  // Loading states
  isLoading: boolean;
  error: string | null;

  // Actions
  refreshData: () => Promise<void>;

  // Subscription management
  createSubscription: (subscription: Omit<RevOpsSubscription, 'id' | 'createdAt'>) => void;
  updateSubscription: (id: string, updates: Partial<RevOpsSubscription>) => void;
  deleteSubscription: (id: string) => void;
  convertWonDealToSubscription: (deal: PipelineOpportunity) => RevOpsSubscription;

  // Invoice management
  createInvoice: (invoice: Omit<RevOpsInvoice, 'id' | 'createdAt' | 'invoiceNumber'>) => RevOpsInvoice;
  updateInvoice: (id: string, updates: Partial<RevOpsInvoice>) => void;
  deleteInvoice: (id: string) => void;
  generateInvoiceFromSubscription: (subscription: RevOpsSubscription, template?: InvoiceTemplate) => RevOpsInvoice;

  // Account management
  createAccount: (account: Omit<RevOpsAccount, 'id'>) => void;
  updateAccount: (id: string, updates: Partial<RevOpsAccount>) => void;

  // Template management
  createTemplate: (template: Omit<InvoiceTemplate, 'id'>) => void;
  updateTemplate: (id: string, updates: Partial<InvoiceTemplate>) => void;
  deleteTemplate: (id: string) => void;
  setDefaultTemplate: (id: string) => void;
  duplicateTemplate: (id: string) => InvoiceTemplate | null;

  // Tax calculation
  calculateTax: (subtotal: number, jurisdiction: string, serviceType?: string) => { rate: number; amount: number; jurisdiction: string };

  // Analytics
  getAnalytics: () => RevOpsAnalytics;
}

export interface RevOpsAnalytics {
  totalMrr: number;
  totalArr: number;
  activeSubscriptions: number;
  activeAccounts: number;
  churnRate: number;
  avgRevenuePerCustomer: number;
  customerLTV: number;
  netRevenueRetention: number;
  revenueByPricingModel: RevenueByPricingModel[];
  mrrMovements: MRRMovement[];
  cohortData: CohortData[];
  forecast: RevenueForecast[];
  wonDealsThisMonth: number;
  wonDealsValue: number;
  paidInvoicesThisMonth: number;
  pendingInvoicesValue: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const defaultRevOpsContext: RevOpsContextValue = {
  organizations: [],
  products: [],
  wonDeals: [],
  subscriptions: [],
  invoices: [],
  accounts: [],
  invoiceTemplates: [],
  isLoading: true,
  error: null,
  refreshData: async () => {},
  createSubscription: () => {},
  updateSubscription: () => {},
  deleteSubscription: () => {},
  convertWonDealToSubscription: () => ({} as RevOpsSubscription),
  createInvoice: () => ({} as RevOpsInvoice),
  updateInvoice: () => {},
  deleteInvoice: () => {},
  generateInvoiceFromSubscription: () => ({} as RevOpsInvoice),
  createAccount: () => {},
  updateAccount: () => {},
  createTemplate: () => {},
  updateTemplate: () => {},
  deleteTemplate: () => {},
  setDefaultTemplate: () => {},
  duplicateTemplate: () => null,
  calculateTax: () => ({ rate: 0, amount: 0, jurisdiction: '' }),
  getAnalytics: () => ({} as RevOpsAnalytics),
};

// ============================================================================
// DEFAULT INVOICE TEMPLATE
// ============================================================================

const defaultInvoiceTemplate: InvoiceTemplate = {
  id: 'default',
  name: 'Default Template',
  description: 'Standard invoice template',
  companyName: 'Your Company Name',
  companyAddress: '123 Business St, Suite 100\nCity, State 12345',
  companyPhone: '(555) 123-4567',
  companyEmail: 'billing@yourcompany.com',
  companyWebsite: 'www.yourcompany.com',
  primaryColor: '#2563eb',
  secondaryColor: '#1e40af',
  accentColor: '#3b82f6',
  footerText: 'Thank you for your business!',
  paymentInstructions: 'Payment is due within 30 days. Please include invoice number with payment.',
  termsAndConditions: 'All sales are final. Late payments may incur additional fees.',
  defaultDueDays: 30,
  acceptedPaymentMethods: ['bank_transfer', 'credit_card', 'check'],
  showLogo: true,
  showPaymentInstructions: true,
  showBankDetails: false,
  showTaxId: false,
  invoicePrefix: 'INV',
  isDefault: true,
  createdAt: new Date().toISOString(),
};

// ============================================================================
// SERVER <-> FRONTEND TEMPLATE CONVERTERS
// ============================================================================

function serverToFrontendTemplate(s: ServerInvoiceTemplate): InvoiceTemplate {
  return {
    id: String(s.id),
    name: s.name,
    description: s.description,
    logoUrl: s.logo_url,
    companyName: s.company_name || 'Your Company Name',
    companyAddress: s.company_address,
    companyPhone: s.company_phone,
    companyEmail: s.company_email,
    companyWebsite: s.company_website,
    primaryColor: s.primary_color || '#2563eb',
    secondaryColor: s.secondary_color || '#1e40af',
    accentColor: s.accent_color,
    fontFamily: s.font_family,
    bankName: s.bank_name,
    bankAccountName: s.bank_account_name,
    bankAccountNumber: s.bank_account_number,
    bankRoutingNumber: s.bank_routing_number,
    bankSwiftCode: s.bank_swift_code,
    bankIban: s.bank_iban,
    paypalEmail: s.paypal_email,
    stripeEnabled: s.stripe_enabled,
    acceptedPaymentMethods: (s.accepted_payment_methods || []) as InvoiceTemplate['acceptedPaymentMethods'],
    paymentMethodInstructions: s.payment_method_instructions,
    footerText: s.footer_text,
    paymentInstructions: s.payment_instructions,
    termsAndConditions: s.terms_and_conditions,
    defaultDueDays: s.default_due_days,
    defaultNotes: s.default_notes,
    showLogo: s.show_logo,
    showPaymentInstructions: s.show_payment_instructions,
    showBankDetails: s.show_bank_details,
    showTaxId: s.show_tax_id,
    showPortalLink: s.show_portal_link,
    taxIdLabel: s.tax_id_label,
    taxId: s.tax_id,
    invoicePrefix: s.invoice_prefix,
    invoiceNumberFormat: s.invoice_number_format,
    organizationId: s.cbo_id,
    isDefault: s.is_default,
    createdAt: s.created_at,
    updatedAt: s.updated_at,
  };
}

function frontendToServerTemplate(t: Partial<InvoiceTemplate>): Partial<ServerInvoiceTemplate> {
  const result: Record<string, any> = {};
  if (t.name !== undefined) result.name = t.name;
  if (t.description !== undefined) result.description = t.description;
  if (t.isDefault !== undefined) result.is_default = t.isDefault;
  if (t.logoUrl !== undefined) result.logo_url = t.logoUrl;
  if (t.companyName !== undefined) result.company_name = t.companyName;
  if (t.companyAddress !== undefined) result.company_address = t.companyAddress;
  if (t.companyPhone !== undefined) result.company_phone = t.companyPhone;
  if (t.companyEmail !== undefined) result.company_email = t.companyEmail;
  if (t.companyWebsite !== undefined) result.company_website = t.companyWebsite;
  if (t.primaryColor !== undefined) result.primary_color = t.primaryColor;
  if (t.secondaryColor !== undefined) result.secondary_color = t.secondaryColor;
  if (t.accentColor !== undefined) result.accent_color = t.accentColor;
  if (t.fontFamily !== undefined) result.font_family = t.fontFamily;
  if (t.bankName !== undefined) result.bank_name = t.bankName;
  if (t.bankAccountName !== undefined) result.bank_account_name = t.bankAccountName;
  if (t.bankAccountNumber !== undefined) result.bank_account_number = t.bankAccountNumber;
  if (t.bankRoutingNumber !== undefined) result.bank_routing_number = t.bankRoutingNumber;
  if (t.bankSwiftCode !== undefined) result.bank_swift_code = t.bankSwiftCode;
  if (t.bankIban !== undefined) result.bank_iban = t.bankIban;
  if (t.paypalEmail !== undefined) result.paypal_email = t.paypalEmail;
  if (t.stripeEnabled !== undefined) result.stripe_enabled = t.stripeEnabled;
  if (t.acceptedPaymentMethods !== undefined) result.accepted_payment_methods = t.acceptedPaymentMethods;
  if (t.paymentMethodInstructions !== undefined) result.payment_method_instructions = t.paymentMethodInstructions;
  if (t.paymentInstructions !== undefined) result.payment_instructions = t.paymentInstructions;
  if (t.termsAndConditions !== undefined) result.terms_and_conditions = t.termsAndConditions;
  if (t.footerText !== undefined) result.footer_text = t.footerText;
  if (t.defaultNotes !== undefined) result.default_notes = t.defaultNotes;
  if (t.defaultDueDays !== undefined) result.default_due_days = t.defaultDueDays;
  if (t.taxId !== undefined) result.tax_id = t.taxId;
  if (t.taxIdLabel !== undefined) result.tax_id_label = t.taxIdLabel;
  if (t.invoicePrefix !== undefined) result.invoice_prefix = t.invoicePrefix;
  if (t.invoiceNumberFormat !== undefined) result.invoice_number_format = t.invoiceNumberFormat;
  if (t.showLogo !== undefined) result.show_logo = t.showLogo;
  if (t.showPaymentInstructions !== undefined) result.show_payment_instructions = t.showPaymentInstructions;
  if (t.showBankDetails !== undefined) result.show_bank_details = t.showBankDetails;
  if (t.showTaxId !== undefined) result.show_tax_id = t.showTaxId;
  if (t.showPortalLink !== undefined) result.show_portal_link = t.showPortalLink;
  return result as Partial<ServerInvoiceTemplate>;
}

// ============================================================================
// CONTEXT
// ============================================================================

export const RevOpsContext = createContext<RevOpsContextValue>(defaultRevOpsContext);

// ============================================================================
// PROVIDER
// ============================================================================

interface RevOpsProviderProps {
  children: ReactNode;
}

export function RevOpsProvider({ children }: RevOpsProviderProps) {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [wonDeals, setWonDeals] = useState<PipelineOpportunity[]>([]);
  const [subscriptions, setSubscriptions] = useState<RevOpsSubscription[]>([]);
  const [invoices, setInvoices] = useState<RevOpsInvoice[]>([]);
  const [accounts, setAccounts] = useState<RevOpsAccount[]>([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState<InvoiceTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invoice number counter
  const [invoiceCounter, setInvoiceCounter] = useState(1);

  /**
   * Fetch organizations from API
   */
  const fetchOrganizations = async (): Promise<Organization[]> => {
    try {
      const response = await apiClient.get('/api/cbos');
      return response.data.map((item: any) => ({
        id: item.id,
        name: item.name,
        short_name: item.short_name,
        customer_status: item.customer_status || 'OPPORTUNITY',
        created_at: item.created_at,
        city: item.city,
        state: item.state,
        website: item.website,
        logo_url: item.logo_url,
        billing_email: item.billing_email,
        billing_address: item.billing_address,
        tax_id: item.tax_id,
      }));
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      return [];
    }
  };

  /**
   * Load all data from localStorage and API
   * Uses salesPipelineAPI for centralized data access with backend/localStorage fallback
   */
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch organizations from API
      const orgs = await fetchOrganizations();
      setOrganizations(orgs);

      // Load persisted RevOps data from localStorage (org-specific keys only)
      const activeCboForStorage = localStorage.getItem('active_cbo_id');
      const orgSuffix = activeCboForStorage ? `_${activeCboForStorage}` : null;
      // Only load cached data if we have an org-specific key — never read old non-prefixed keys
      const storedInvoices = orgSuffix ? localStorage.getItem(`revops_invoices${orgSuffix}`) : null;
      const storedAccounts = orgSuffix ? localStorage.getItem(`revops_accounts${orgSuffix}`) : null;
      const storedTemplates = orgSuffix ? localStorage.getItem(`revops_templates${orgSuffix}`) : null;
      const storedInvoiceCounter = orgSuffix ? localStorage.getItem(`revops_invoice_counter${orgSuffix}`) : null;

      // Load products from Sales Pipeline catalog using the API
      const storedPipelineProducts = localStorage.getItem(salesPipelineAPI.STORAGE_KEYS.PRODUCTS);
      if (storedPipelineProducts) {
        setProducts(JSON.parse(storedPipelineProducts));
      }

      // Load opportunities from Sales Pipeline to find won deals using the API
      const storedPipelines = localStorage.getItem(salesPipelineAPI.STORAGE_KEYS.PIPELINES);
      const storedOpportunities = localStorage.getItem(salesPipelineAPI.STORAGE_KEYS.OPPORTUNITIES);

      if (storedOpportunities) {
        const allOpportunities = JSON.parse(storedOpportunities) as PipelineOpportunity[];
        // Find won deals (stage is 'closed_won' or stage has isWon: true)
        let wonStageIds = ['closed_won'];

        // Get won stage IDs from pipeline config
        if (storedPipelines) {
          const pipelines = JSON.parse(storedPipelines);
          pipelines.forEach((pipeline: any) => {
            pipeline.stages?.forEach((stage: any) => {
              if (stage.isWon) {
                wonStageIds.push(stage.id);
              }
            });
          });
        }

        const wonOpportunities = allOpportunities.filter(opp =>
          wonStageIds.includes(opp.stage)
        );
        setWonDeals(wonOpportunities);
      }

      // Subscriptions now come from the real platform API (ro_subscriptions), not the
      // old per-browser localStorage store, so the overview/analytics/billing tabs
      // reflect actual subscriptions. Best-effort: empty on any error.
      try {
        const { listSubscriptions } = await import('@/services/api/platformSubscriptions');
        const apiSubs = await listSubscriptions();
        setSubscriptions(apiSubs.map((s) => ({
          id: s.id,
          customer: '',
          customerId: undefined,
          opportunityId: s.opportunityId || undefined,
          plan: s.planName || 'Custom',
          mrr: s.mrr || 0,
          arr: s.arr || 0,
          status: (s.status === 'canceled' ? 'cancelled' : s.status) as RevOpsSubscription['status'],
          startDate: s.startDate || '',
          renewalDate: s.currentPeriodEnd || '',
          billingInterval: s.billingInterval,
          createdAt: s.createdAt,
        })));
      } catch (e) {
        console.error('Failed to load subscriptions from API:', e);
        setSubscriptions([]);
      }

      // Fetch real invoices from the invoice management API and merge with localStorage
      const cboIdForInvoices = localStorage.getItem('active_cbo_id');
      let realInvoices: RevOpsInvoice[] = [];
      if (cboIdForInvoices) {
        try {
          const apiResult = await invoiceAPI.listInvoices(Number(cboIdForInvoices), { page_size: 100 });
          realInvoices = apiResult.invoices.map((inv: APIInvoice): RevOpsInvoice => {
            // Map API status to RevOps status
            const statusMap: Record<string, RevOpsInvoice['status']> = {
              draft: 'draft', open: 'pending', sent: 'sent', viewed: 'pending',
              overdue: 'overdue', paid: 'paid', void: 'void', uncollectible: 'void',
            };
            return {
              id: String(inv.id),
              invoiceNumber: inv.invoice_number,
              customer: inv.customer_snapshot?.name || inv.customer_snapshot?.company || 'Customer',
              customerId: inv.customer_contact_id,
              opportunityId: inv.opportunity_id,
              amount: centsToDecimal(inv.total_cents),
              subtotal: centsToDecimal(inv.subtotal_cents),
              tax: centsToDecimal(inv.tax_cents),
              discount: centsToDecimal(inv.discount_cents),
              status: statusMap[inv.status] || 'pending',
              dueDate: inv.due_date || '',
              issuedDate: inv.invoice_date || '',
              paidDate: inv.paid_at,
              notes: inv.notes,
              createdAt: inv.finalized_at || inv.invoice_date,
            };
          });
        } catch (err) {
          console.warn('Failed to fetch invoices from API for RevOps analytics:', err);
        }
      }

      // Merge: real invoices take precedence, then add any localStorage-only invoices
      if (storedInvoices) {
        const invs = JSON.parse(storedInvoices) as RevOpsInvoice[];
        const realIds = new Set(realInvoices.map(i => i.id));
        const localOnly = invs.filter(inv => !realIds.has(inv.id));
        const merged = [...realInvoices, ...localOnly].map(inv => ({
          ...inv,
          organization: orgs.find(o => o.id === inv.customerId),
        }));
        setInvoices(merged);
      } else {
        setInvoices(realInvoices.map(inv => ({
          ...inv,
          organization: orgs.find(o => o.id === inv.customerId),
        })));
      }

      if (storedAccounts) {
        const accts = JSON.parse(storedAccounts);
        setAccounts(accts.map((acct: RevOpsAccount) => ({
          ...acct,
          organization: orgs.find(o => o.id === acct.organizationId),
        })));
      }

      // Load templates from server API, fallback to localStorage
      const cboId = localStorage.getItem('active_cbo_id');
      if (cboId) {
        try {
          const serverTemplates = await invoiceAPI.listTemplates(Number(cboId));
          if (serverTemplates.length > 0) {
            setInvoiceTemplates(serverTemplates.map(serverToFrontendTemplate));
          } else {
            // Server returned no templates for this org — clear any defaults
            setInvoiceTemplates([]);
          }
        } catch {
          // Server unavailable — use localStorage cache
          if (storedTemplates) {
            setInvoiceTemplates(JSON.parse(storedTemplates));
          }
        }
      } else if (storedTemplates) {
        setInvoiceTemplates(JSON.parse(storedTemplates));
      }

      if (storedInvoiceCounter) {
        setInvoiceCounter(parseInt(storedInvoiceCounter, 10));
      }

      // Only auto-create accounts from customer orgs for the platform operator (Trust Care, cbo_id=1)
      // Other orgs should start with empty accounts until they create their own
      const activeCboId = localStorage.getItem('active_cbo_id');
      const isPlatformOp = !activeCboId || activeCboId === '1';

      if (!storedAccounts) {
        if (isPlatformOp) {
          const customerOrgs = orgs.filter(o => o.customer_status === 'CUSTOMER');
          const initialAccounts: RevOpsAccount[] = customerOrgs.map(org => ({
            id: `acct_${org.id}`,
            organizationId: org.id,
            name: org.name,
            status: 'active' as const,
            mrr: 0,
            arr: 0,
            startDate: org.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
            source: 'pipeline_won' as const,
            organization: org,
          }));
          setAccounts(initialAccounts);
        } else {
          setAccounts([]);
        }
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load RevOps data';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Persist data to localStorage
   * Note: Products and won deals are managed by Sales Pipeline, not persisted here
   */
  const persistData = useCallback(() => {
    const activeCbo = localStorage.getItem('active_cbo_id');
    if (!activeCbo) return; // Don't persist without org context
    const orgSuffix = `_${activeCbo}`;
    // Subscriptions are now sourced from the platform API (not persisted here).
    localStorage.setItem(`revops_invoices${orgSuffix}`, JSON.stringify(invoices));
    localStorage.setItem(`revops_accounts${orgSuffix}`, JSON.stringify(accounts));
    localStorage.setItem(`revops_templates${orgSuffix}`, JSON.stringify(invoiceTemplates));
    localStorage.setItem(`revops_invoice_counter${orgSuffix}`, String(invoiceCounter));
  }, [subscriptions, invoices, accounts, invoiceTemplates, invoiceCounter]);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Persist data when it changes
  useEffect(() => {
    if (!isLoading) {
      persistData();
    }
  }, [subscriptions, invoices, accounts, invoiceTemplates, invoiceCounter, isLoading, persistData]);

  /**
   * Refresh all data
   */
  const refreshData = async () => {
    await loadData();
  };

  /**
   * Create a new subscription
   */
  const createSubscription = (subscriptionData: Omit<RevOpsSubscription, 'id' | 'createdAt'>) => {
    const newSubscription: RevOpsSubscription = {
      ...subscriptionData,
      id: `sub_${Date.now()}`,
      createdAt: new Date().toISOString(),
      organization: organizations.find(o => o.id === subscriptionData.customerId),
    };
    setSubscriptions(prev => [newSubscription, ...prev]);
  };

  /**
   * Update a subscription
   */
  const updateSubscription = (id: string, updates: Partial<RevOpsSubscription>) => {
    setSubscriptions(prev => prev.map(sub =>
      sub.id === id ? { ...sub, ...updates } : sub
    ));
  };

  /**
   * Delete a subscription
   */
  const deleteSubscription = (id: string) => {
    setSubscriptions(prev => prev.filter(sub => sub.id !== id));
  };

  /**
   * Convert a won deal to a subscription
   */
  const convertWonDealToSubscription = (deal: PipelineOpportunity): RevOpsSubscription => {
    const financials = deal.financials;
    const termMonths = deal.contractTermMonths || 12;

    // Calculate renewal date
    const startDate = new Date();
    const renewalDate = new Date(startDate);
    renewalDate.setMonth(renewalDate.getMonth() + termMonths);

    // Convert line items
    const subscriptionLineItems: SubscriptionLineItem[] = (deal.lineItems || []).map(item => ({
      id: `sli_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      itemName: item.itemName,
      description: item.description,
      pricingModel: item.pricingModel,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      billingType: item.billingType,
      billingInterval: item.billingInterval,
      transactionRate: item.transactionRate,
      pricePerOutcome: item.pricePerOutcome,
      monthlyRecurring: item.monthlyRecurring,
      annualRecurring: item.annualRecurring,
    }));

    const newSubscription: RevOpsSubscription = {
      id: `sub_${Date.now()}`,
      customer: deal.company,
      customerId: deal.companyId,
      opportunityId: deal.id,
      plan: 'Custom',
      mrr: financials?.totalMrr || deal.amount / 12,
      arr: financials?.totalArr || deal.amount,
      status: 'active',
      startDate: startDate.toISOString().split('T')[0],
      renewalDate: renewalDate.toISOString().split('T')[0],
      contractTermMonths: termMonths,
      lineItems: subscriptionLineItems,
      oneTimeRevenue: financials?.totalOneTime || 0,
      estimatedUsageRevenue: financials?.estimatedUsageRevenue || 0,
      estimatedTransactionRevenue: financials?.estimatedTransactionRevenue || 0,
      estimatedOutcomeRevenue: financials?.estimatedOutcomeRevenue || 0,
      organization: organizations.find(o => o.id === deal.companyId),
      createdAt: new Date().toISOString(),
    };

    setSubscriptions(prev => [newSubscription, ...prev]);

    // Also create an account
    createAccount({
      organizationId: deal.companyId,
      name: deal.company,
      status: 'active',
      mrr: newSubscription.mrr,
      arr: newSubscription.arr,
      startDate: newSubscription.startDate,
      source: 'pipeline_won',
      opportunityId: deal.id,
      contractTermMonths: termMonths,
    });

    return newSubscription;
  };

  /**
   * Generate invoice number
   */
  const generateInvoiceNumber = (): string => {
    const year = new Date().getFullYear();
    const number = String(invoiceCounter).padStart(4, '0');
    setInvoiceCounter(prev => prev + 1);
    return `INV-${year}-${number}`;
  };

  /**
   * Create a new invoice
   */
  const createInvoice = (invoiceData: Omit<RevOpsInvoice, 'id' | 'createdAt' | 'invoiceNumber'>): RevOpsInvoice => {
    const newInvoice: RevOpsInvoice = {
      ...invoiceData,
      id: `inv_${Date.now()}`,
      invoiceNumber: generateInvoiceNumber(),
      createdAt: new Date().toISOString(),
      organization: organizations.find(o => o.id === invoiceData.customerId),
      template: invoiceData.template || invoiceTemplates.find(t => t.isDefault),
    };
    setInvoices(prev => [newInvoice, ...prev]);
    return newInvoice;
  };

  /**
   * Update an invoice
   */
  const updateInvoice = (id: string, updates: Partial<RevOpsInvoice>) => {
    setInvoices(prev => prev.map(inv =>
      inv.id === id ? { ...inv, ...updates } : inv
    ));
  };

  /**
   * Delete an invoice
   */
  const deleteInvoice = (id: string) => {
    setInvoices(prev => prev.filter(inv => inv.id !== id));
  };

  /**
   * Generate invoice from subscription
   */
  const generateInvoiceFromSubscription = (
    subscription: RevOpsSubscription,
    template?: InvoiceTemplate
  ): RevOpsInvoice => {
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 30);

    // Create line items from subscription
    const lineItems: InvoiceLineItem[] = subscription.lineItems?.map(item => ({
      id: `ili_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      description: item.itemName,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      amount: item.monthlyRecurring,
      pricingModel: item.pricingModel,
      billingType: item.billingType,
    })) || [{
      id: `ili_${Date.now()}`,
      description: `${subscription.plan} - Monthly Subscription`,
      quantity: 1,
      unitPrice: subscription.mrr,
      amount: subscription.mrr,
    }];

    const subtotal = lineItems.reduce((sum, item) => sum + item.amount, 0);

    return createInvoice({
      customer: subscription.customer,
      customerId: subscription.customerId,
      subscriptionId: subscription.id,
      amount: subtotal,
      subtotal,
      status: 'draft',
      dueDate: dueDate.toISOString().split('T')[0],
      issuedDate: new Date().toISOString().split('T')[0],
      lineItems,
      template: template || invoiceTemplates.find(t => t.isDefault),
    });
  };

  /**
   * Create a new account
   */
  const createAccount = (accountData: Omit<RevOpsAccount, 'id'>) => {
    // Check if account already exists for this organization
    const existing = accounts.find(a => a.organizationId === accountData.organizationId);
    if (existing) {
      updateAccount(existing.id, accountData);
      return;
    }

    const newAccount: RevOpsAccount = {
      ...accountData,
      id: `acct_${Date.now()}`,
      organization: organizations.find(o => o.id === accountData.organizationId),
    };
    setAccounts(prev => [newAccount, ...prev]);
  };

  /**
   * Update an account
   */
  const updateAccount = (id: string, updates: Partial<RevOpsAccount>) => {
    setAccounts(prev => prev.map(acct =>
      acct.id === id ? { ...acct, ...updates } : acct
    ));
  };

  /**
   * Create a new template (persists to server)
   */
  const createTemplate = async (templateData: Omit<InvoiceTemplate, 'id'>) => {
    const cboId = localStorage.getItem('active_cbo_id');
    if (cboId) {
      try {
        const serverData = frontendToServerTemplate(templateData) as any;
        const created = await invoiceAPI.createTemplate(Number(cboId), serverData);
        const frontendTemplate = serverToFrontendTemplate(created);
        setInvoiceTemplates(prev => [...prev, frontendTemplate]);
        return;
      } catch (err) {
        console.error('Failed to save template to server:', err);
      }
    }
    // Fallback: local-only
    const newTemplate: InvoiceTemplate = {
      ...templateData,
      id: `tmpl_${Date.now()}`,
    };
    setInvoiceTemplates(prev => [...prev, newTemplate]);
  };

  /**
   * Update a template (persists to server)
   */
  const updateTemplate = async (id: string, updates: Partial<InvoiceTemplate>) => {
    // Optimistic update
    setInvoiceTemplates(prev => prev.map(tmpl =>
      tmpl.id === id ? { ...tmpl, ...updates } : tmpl
    ));
    const cboId = localStorage.getItem('active_cbo_id');
    const numericId = Number(id);
    if (cboId && !isNaN(numericId)) {
      try {
        const serverData = frontendToServerTemplate(updates) as any;
        await invoiceAPI.updateTemplate(Number(cboId), numericId, serverData);
      } catch (err) {
        console.error('Failed to update template on server:', err);
      }
    }
  };

  /**
   * Set default template (persists to server)
   */
  const setDefaultTemplate = async (id: string) => {
    setInvoiceTemplates(prev => prev.map(tmpl => ({
      ...tmpl,
      isDefault: tmpl.id === id,
    })));
    const cboId = localStorage.getItem('active_cbo_id');
    const numericId = Number(id);
    if (cboId && !isNaN(numericId)) {
      try {
        await invoiceAPI.updateTemplate(Number(cboId), numericId, { is_default: true } as any);
      } catch (err) {
        console.error('Failed to set default template on server:', err);
      }
    }
  };

  /**
   * Delete a template (persists to server)
   */
  const deleteTemplate = async (id: string) => {
    const template = invoiceTemplates.find(t => t.id === id);
    if (template?.isDefault) {
      console.warn('Cannot delete the default template');
      return;
    }
    setInvoiceTemplates(prev => prev.filter(tmpl => tmpl.id !== id));
    const cboId = localStorage.getItem('active_cbo_id');
    const numericId = Number(id);
    if (cboId && !isNaN(numericId)) {
      try {
        await invoiceAPI.deleteTemplate(Number(cboId), numericId);
      } catch (err) {
        console.error('Failed to delete template on server:', err);
      }
    }
  };

  /**
   * Duplicate a template (persists to server)
   */
  const duplicateTemplate = (id: string): InvoiceTemplate | null => {
    const original = invoiceTemplates.find(t => t.id === id);
    if (!original) return null;

    const tempId = `tmpl_${Date.now()}`;
    const newTemplate: InvoiceTemplate = {
      ...original,
      id: tempId,
      name: `${original.name} (Copy)`,
      isDefault: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setInvoiceTemplates(prev => [...prev, newTemplate]);

    // Persist to server in background
    const cboId = localStorage.getItem('active_cbo_id');
    if (cboId) {
      const serverData = frontendToServerTemplate({ ...newTemplate, isDefault: false }) as any;
      invoiceAPI.createTemplate(Number(cboId), serverData).then(created => {
        // Replace temp ID with server-assigned ID
        const frontendCreated = serverToFrontendTemplate(created);
        setInvoiceTemplates(prev => prev.map(t =>
          t.id === tempId ? frontendCreated : t
        ));
      }).catch(err => {
        console.error('Failed to duplicate template on server:', err);
      });
    }

    return newTemplate;
  };

  /**
   * Calculate tax based on jurisdiction
   * This is a simplified implementation - in production, integrate with a tax API
   */
  const calculateTax = (
    subtotal: number,
    jurisdiction: string,
    serviceType?: string
  ): { rate: number; amount: number; jurisdiction: string } => {
    // US State tax rates (simplified - real implementation should use tax API)
    const stateTaxRates: Record<string, number> = {
      'CA': 7.25, 'NY': 8.0, 'TX': 6.25, 'FL': 6.0, 'WA': 6.5,
      'IL': 6.25, 'PA': 6.0, 'OH': 5.75, 'GA': 4.0, 'NC': 4.75,
      'NJ': 6.625, 'VA': 5.3, 'AZ': 5.6, 'MA': 6.25, 'TN': 7.0,
      'IN': 7.0, 'MO': 4.225, 'MD': 6.0, 'WI': 5.0, 'CO': 2.9,
      'MN': 6.875, 'SC': 6.0, 'AL': 4.0, 'LA': 4.45, 'KY': 6.0,
      'OR': 0, 'OK': 4.5, 'CT': 6.35, 'UT': 4.85, 'IA': 6.0,
      'NV': 6.85, 'AR': 6.5, 'MS': 7.0, 'KS': 6.5, 'NM': 5.125,
      'NE': 5.5, 'WV': 6.0, 'ID': 6.0, 'HI': 4.0, 'NH': 0,
      'ME': 5.5, 'MT': 0, 'RI': 7.0, 'DE': 0, 'SD': 4.5,
      'ND': 5.0, 'AK': 0, 'VT': 6.0, 'WY': 4.0,
    };

    // Some services may be exempt or have different rates
    const exemptServices = ['healthcare', 'education', 'nonprofit'];
    if (serviceType && exemptServices.includes(serviceType.toLowerCase())) {
      return { rate: 0, amount: 0, jurisdiction };
    }

    // Get the state code from jurisdiction (e.g., "CA" or "California, CA")
    const stateCode = jurisdiction.length === 2
      ? jurisdiction.toUpperCase()
      : jurisdiction.split(',').pop()?.trim().toUpperCase() || '';

    const rate = stateTaxRates[stateCode] || 0;
    const amount = Math.round((subtotal * rate / 100) * 100) / 100;

    return { rate, amount, jurisdiction: stateCode };
  };

  /**
   * Calculate analytics
   */
  const getAnalytics = (): RevOpsAnalytics => {
    const activeSubscriptions = subscriptions.filter(s => s.status === 'active');
    const activeAccountsList = accounts.filter(a => a.status === 'active');

    const totalMrr = activeSubscriptions.reduce((sum, s) => sum + s.mrr, 0);
    const totalArr = activeSubscriptions.reduce((sum, s) => sum + s.arr, 0);
    const activeCount = activeSubscriptions.length;
    const activeAccountsCount = activeAccountsList.length;

    const avgRevenuePerCustomer = activeCount > 0 ? Math.round(totalMrr / activeCount) : 0;
    const avgLifespanMonths = 24;
    const customerLTV = avgRevenuePerCustomer * avgLifespanMonths;

    // Calculate revenue by pricing model
    const pricingModelRevenue: Record<string, number> = {};
    activeSubscriptions.forEach(sub => {
      sub.lineItems?.forEach(item => {
        const model = item.pricingModel || 'flat_rate';
        pricingModelRevenue[model] = (pricingModelRevenue[model] || 0) + item.monthlyRecurring;
      });
    });

    const modelColors: Record<string, string> = {
      'per_user': 'bg-blue-500',
      'per_member': 'bg-green-500',
      'transaction': 'bg-purple-500',
      'flat_rate': 'bg-orange-500',
      'outcome_based': 'bg-pink-500',
      'usage_based': 'bg-cyan-500',
      'tiered': 'bg-yellow-500',
      'volume': 'bg-red-500',
      'percentage_of_billing': 'bg-indigo-500',
    };

    const modelLabels: Record<string, string> = {
      'per_user': 'Per User (SaaS)',
      'per_member': 'Per Member',
      'transaction': 'Transaction-Based',
      'flat_rate': 'Flat Rate',
      'outcome_based': 'Outcome-Based',
      'usage_based': 'Usage-Based',
      'tiered': 'Tiered',
      'volume': 'Volume',
      'percentage_of_billing': '% of Billing',
    };

    const revenueByPricingModel: RevenueByPricingModel[] = Object.entries(pricingModelRevenue)
      .map(([model, mrr]) => ({
        model,
        label: modelLabels[model] || model,
        mrr,
        percentage: totalMrr > 0 ? Math.round((mrr / totalMrr) * 100) : 0,
        color: modelColors[model] || 'bg-gray-500',
      }))
      .sort((a, b) => b.mrr - a.mrr);

    // If no breakdown, use estimated distribution
    if (revenueByPricingModel.length === 0 && totalMrr > 0) {
      revenueByPricingModel.push(
        { model: 'per_user', label: 'Per User (SaaS)', mrr: Math.round(totalMrr * 0.45), percentage: 45, color: 'bg-blue-500' },
        { model: 'per_member', label: 'Per Member', mrr: Math.round(totalMrr * 0.25), percentage: 25, color: 'bg-green-500' },
        { model: 'transaction', label: 'Transaction-Based', mrr: Math.round(totalMrr * 0.15), percentage: 15, color: 'bg-purple-500' },
        { model: 'flat_rate', label: 'Flat Rate', mrr: Math.round(totalMrr * 0.10), percentage: 10, color: 'bg-orange-500' },
        { model: 'outcome_based', label: 'Outcome-Based', mrr: Math.round(totalMrr * 0.05), percentage: 5, color: 'bg-pink-500' },
      );
    }

    // MRR movements (simplified calculation)
    const thisMonth = new Date().toISOString().slice(0, 7);
    const newSubscriptionsThisMonth = subscriptions.filter(s =>
      s.createdAt?.startsWith(thisMonth) && s.status === 'active'
    );
    const newMrr = newSubscriptionsThisMonth.reduce((sum, s) => sum + s.mrr, 0);

    const mrrMovements: MRRMovement[] = [
      { type: 'new', label: 'New Business', amount: newMrr || Math.round(totalMrr * 0.1), count: newSubscriptionsThisMonth.length || 2 },
      { type: 'expansion', label: 'Expansion', amount: Math.round(totalMrr * 0.05), count: 1 },
      { type: 'reactivation', label: 'Reactivation', amount: Math.round(totalMrr * 0.02), count: 1 },
      { type: 'contraction', label: 'Contraction', amount: -Math.round(totalMrr * 0.02), count: 1 },
      { type: 'churn', label: 'Churn', amount: -Math.round(totalMrr * 0.03), count: 1 },
    ];

    // Cohort data (simulated)
    const cohortData: CohortData[] = [
      { cohort: 'Jan 2024', month0: 100, month1: 95, month2: 92, month3: 88, month6: 82, month12: 75 },
      { cohort: 'Feb 2024', month0: 100, month1: 94, month2: 90, month3: 87, month6: 80, month12: 72 },
      { cohort: 'Mar 2024', month0: 100, month1: 96, month2: 93, month3: 90, month6: 85, month12: 78 },
      { cohort: 'Apr 2024', month0: 100, month1: 93, month2: 89, month3: 85, month6: 78, month12: 0 },
      { cohort: 'May 2024', month0: 100, month1: 95, month2: 91, month3: 88, month6: 82, month12: 0 },
      { cohort: 'Jun 2024', month0: 100, month1: 97, month2: 94, month3: 91, month6: 0, month12: 0 },
    ];

    // Forecast
    const forecast: RevenueForecast[] = [
      { month: 'Jan', projected: totalMrr, optimistic: Math.round(totalMrr * 1.05), conservative: Math.round(totalMrr * 0.95) },
      { month: 'Feb', projected: Math.round(totalMrr * 1.03), optimistic: Math.round(totalMrr * 1.08), conservative: Math.round(totalMrr * 0.97) },
      { month: 'Mar', projected: Math.round(totalMrr * 1.06), optimistic: Math.round(totalMrr * 1.12), conservative: Math.round(totalMrr * 0.98) },
      { month: 'Apr', projected: Math.round(totalMrr * 1.09), optimistic: Math.round(totalMrr * 1.16), conservative: Math.round(totalMrr * 0.99) },
      { month: 'May', projected: Math.round(totalMrr * 1.12), optimistic: Math.round(totalMrr * 1.20), conservative: Math.round(totalMrr * 1.00) },
      { month: 'Jun', projected: Math.round(totalMrr * 1.15), optimistic: Math.round(totalMrr * 1.25), conservative: Math.round(totalMrr * 1.02) },
    ];

    // Won deals and invoice metrics
    const paidInvoicesThisMonth = invoices.filter(i =>
      i.status === 'paid' && i.paidDate?.startsWith(thisMonth)
    );
    const pendingInvoices = invoices.filter(i => i.status === 'pending' || i.status === 'sent');

    return {
      totalMrr,
      totalArr,
      activeSubscriptions: activeCount,
      activeAccounts: activeAccountsCount,
      churnRate: 2.1,
      avgRevenuePerCustomer,
      customerLTV,
      netRevenueRetention: 115,
      revenueByPricingModel,
      mrrMovements,
      cohortData,
      forecast,
      wonDealsThisMonth: wonDeals.filter(d => d.stage === 'closed_won').length,
      wonDealsValue: wonDeals.filter(d => d.stage === 'closed_won').reduce((sum, d) => sum + d.amount, 0),
      paidInvoicesThisMonth: paidInvoicesThisMonth.length,
      pendingInvoicesValue: pendingInvoices.reduce((sum, i) => sum + i.amount, 0),
    };
  };

  const contextValue: RevOpsContextValue = {
    organizations,
    products,
    wonDeals,
    subscriptions,
    invoices,
    accounts,
    invoiceTemplates,
    isLoading,
    error,
    refreshData,
    createSubscription,
    updateSubscription,
    deleteSubscription,
    convertWonDealToSubscription,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    generateInvoiceFromSubscription,
    createAccount,
    updateAccount,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
    duplicateTemplate,
    calculateTax,
    getAnalytics,
  };

  return (
    <RevOpsContext.Provider value={contextValue}>
      {children}
    </RevOpsContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useRevOps(): RevOpsContextValue {
  const context = useContext(RevOpsContext);
  if (context === undefined) {
    throw new Error('useRevOps must be used within a RevOpsProvider');
  }
  return context;
}

export function useSubscriptions() {
  const { subscriptions, createSubscription, updateSubscription, deleteSubscription, convertWonDealToSubscription } = useRevOps();
  return { subscriptions, createSubscription, updateSubscription, deleteSubscription, convertWonDealToSubscription };
}

export function useInvoices() {
  const { invoices, createInvoice, updateInvoice, deleteInvoice, generateInvoiceFromSubscription, invoiceTemplates } = useRevOps();
  return { invoices, createInvoice, updateInvoice, deleteInvoice, generateInvoiceFromSubscription, invoiceTemplates };
}

export function useRevOpsAnalytics() {
  const { getAnalytics, subscriptions, invoices, accounts, wonDeals } = useRevOps();
  return { getAnalytics, subscriptions, invoices, accounts, wonDeals };
}
